import { Skeleton, Typography } from 'antd';
import { RightIcon } from 'assets';
import Avatar from 'components/common/avatar/Avatar';
import dayjs from 'dayjs';
import { aiAgentCenterMessages, omniChatMessages } from 'messages';
import { customerLookupMessages } from 'messages/customer-lookup.message';
import React, { memo } from 'react';
import { ContactDto, ItemOption } from 'types';
import { AIContactEvaluationOption } from 'utils';
import { useContactDetailByChat } from 'hooks/contacts';
import CustomerLookupContactModal from 'components/customer-lookup-contact-histories/CustomerLookupContactModal';
import { useNavigate } from 'react-router-dom';
import { ROUTE } from 'routes/constants';
import { useOmniHistoriesLeadManageContext } from 'contexts';

const ChatContactInfo: React.FC = () => {
  const { chatHistory } = useOmniHistoriesLeadManageContext();
  const { contact: contactDetail, isLoading } = useContactDetailByChat(chatHistory?.chatId!);

  return (
    <div className=''>
      {isLoading ? (
        <Skeleton title={false} paragraph={{ rows: 5 }} />
      ) : (
        <div className='max-h-[calc(100%-64px)] overflow-y-auto py-5'>
          <ContactInformation contact={contactDetail!} />
        </div>
      )}
    </div>
  );
};

const ContactInformation: React.FC<{ contact: ContactDto }> = ({ contact }) => {
  const navigate = useNavigate();

  const [open, setOpen] = React.useState(false);

  const optionEvaluation = contact?.contactSentiment?.sentiment
    ? AIContactEvaluationOption[contact.contactSentiment?.sentiment as keyof typeof AIContactEvaluationOption]
    : null;

  const personalInfoItems: ItemOption[] = [
    {
      label: customerLookupMessages.phoneNumber,
      value: contact?.phoneNumber
    },
    {
      label: customerLookupMessages.idNumber,
      value: contact?.idNumber
    },
    {
      label: customerLookupMessages.dateProvided,
      value: contact?.idIssuedDate ? dayjs(contact?.idIssuedDate).format('DD/MM/YYYY') : ''
    },
    {
      label: customerLookupMessages.placeProvided,
      value: contact?.idIssuedPlace
    }
  ];

  return (
    <div className='border-b px-5 pb-6'>
      <Typography.Text className='font-semibold'>{aiAgentCenterMessages.contactInfo}</Typography.Text>
      {!contact ? (
        <div className='pt-5'>
          <Typography.Text className='text-colorTextPlaceholder'>{omniChatMessages.noContact}</Typography.Text>
        </div>
      ) : (
        <>
          <div className='mt-5 flex w-full flex-col items-center justify-center'>
            <div>
              <div className='relative h-20 w-20 rounded-full'>
                <Avatar size={80} name={contact.name} src={contact.avatarUrl} />
                <div className='absolute bottom-[1.5px] right-[1.5px] h-5 w-5'>
                  <div
                    className='absolute left-0.5 top-0.5 z-0 h-[17px] w-[17px] rounded-full'
                    style={{
                      background: optionEvaluation?.color
                    }}
                  />
                  {optionEvaluation?.icon(21, `text-white absolute left-0 top-0 `)}
                </div>
              </div>
            </div>
            <div className='mb-2 mt-4 items-center justify-center text-center'>
              <Typography.Text className='text-lg font-semibold'>{contact.name}</Typography.Text>
            </div>
            <span
              className='rounded-base px-1.5 text-xs font-semibold uppercase leading-5 text-white'
              style={{
                background: optionEvaluation?.color
              }}
            >
              {optionEvaluation?.label}
            </span>
          </div>
          <div className='mt-6 flex flex-col gap-3'>
            {personalInfoItems.map((item, index) => (
              <div key={`personal-info-${index}`} className='flex gap-4'>
                <div className='w-[88px]'>
                  <Typography.Text className='text-subTextColor'>{item.label}:</Typography.Text>
                </div>
                <div className='flex-1'>
                  <Typography.Text>{item.value || '-'}</Typography.Text>
                </div>
              </div>
            ))}
          </div>

          <div className='mt-6'>
            <Typography.Link
              onClick={() => {
                setOpen(true);
              }}
              className='flex items-center gap-2'
            >
              {aiAgentCenterMessages.contactProfile} <RightIcon height={16} width={16} />
            </Typography.Link>
          </div>
          <CustomerLookupContactModal
            searchCriteria={{}}
            open={open}
            onClose={() => setOpen(false)}
            contactId={contact.contactId}
            handleCustomerCodeClick={(code) => {
              navigate(ROUTE.CUSTOMER_LOOKUP, {
                state: {
                  customerCode: code
                }
              });
            }}
          />
        </>
      )}
    </div>
  );
};

export default memo(ChatContactInfo);
